<script setup lang="ts">
import {
	onBeforeMount,
	onBeforeUnmount,
	onMounted,
	onUnmounted,
	shallowRef,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useBaseStore } from "@/stores/base";
import useToast from "@/ui/composables/useToast";
import useDialog from "@/ui/composables/useDialog";
import { useBasePriceStore } from "@/modules/base_price/store";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import PlanHeaderBar from "@/modules/base_price/components/PlanHeaderBar.vue";
import PlanHierarchySidebar from "@/modules/base_price/components/PlanHierarchySidebar.vue";
import PlanNodeDetailPanel from "@/modules/base_price/components/PlanNodeDetailPanel.vue";
import PlanVersionsDrawer from "@/modules/base_price/components/PlanVersionsDrawer.vue";

const router = useRouter();
const store = useBasePriceStore();
const plan_uuid: string = (useRoute().params.plan_uuid ?? "").toString();

const breadcrumbs = [
	{
		title: "Base price plans",
		disabled: false,
		to: "/base-prices",
	},
	{ title: "Edit plan", disabled: true, to: "" },
];

onBeforeMount(() => (useBaseStore().appFullWidth = true));
onBeforeUnmount(() => {
	useBaseStore().appFullWidth = false;
	store.$reset();
});

/**
 * The flags are shared with the app shell, so leaving the editor without
 * clearing them would open BaseLayout's own drawer — empty — over whatever
 * comes next.
 */
onUnmounted(() => {
	const baseStore = useBaseStore();

	baseStore.showSecondaryDrawer = false;
	baseStore.secondaryDrawerComponent = null;
});

/**
 * Puts the versions panel into the editor's secondary drawer. The component is
 * handed over through the store rather than named in the template because the
 * drawer is the app's shared one — the same two flags any other page sets —
 * even though this page renders the panel itself.
 */
const openVersions = () => {
	const baseStore = useBaseStore();

	baseStore.secondaryDrawerComponent = shallowRef(PlanVersionsDrawer);
	baseStore.showSecondaryDrawer = true;
};

onMounted(async () => {
	// Sequential, not parallel: loadResolvedPlan now sends the plan's target
	// and draft overrides as the preview payload, so it needs loadPlan to
	// have populated those first.
	await store.loadPlan(plan_uuid);
	store.loadResolvedPlan(plan_uuid);
});

/**
 * Nothing in the editor is written until Save version — the target, the books
 * and every override live in the draft until then — so leaving with changes
 * outstanding really does lose them. Asking only when there is something to
 * lose keeps the prompt meaningful rather than a reflex to click through.
 */
const close = async () => {
	if (store.hasUnsavedChanges) {
		const confirmed = await useDialog().confirm({
			title: "Discard your changes?",
			message:
				"The changes you made to this plan haven't been saved. Closing now " +
				"leaves the version as it was when you last saved it.",
			confirmButtonText: "Discard and close",
			isDelete: true,
		});

		if (!confirmed) return;
	}

	router.push("/base-prices");
};

/**
 * Takes the impressions afresh and saves a new version against them, so — like
 * saving — the editor moves to the version that was created. Confirmed because
 * it moves every figure in the plan at once; earlier versions keep theirs.
 */
const refreshImpressions = async () => {
	const confirmed = await useDialog().confirm({
		title: "Refresh impressions?",
		message:
			"This plan prices against the impressions as they stood when this " +
			"version was saved. Refreshing takes what the source says now and " +
			`saves "${store.plan?.name}" as a new version against it, archiving ` +
			"the version you are editing. Every budget, CPM and revenue figure " +
			"will move.\n\n" +
			"Earlier versions keep the impressions they were saved with.",
		confirmButtonText: "Refresh and save version",
	});

	if (!confirmed) return;

	const response = await store.refreshImpressions();
	if (!response) return;

	if (response.isErr()) {
		useToast().error(response.error.message);

		return;
	}

	useToast().success("Impressions refreshed.");

	await router.replace(`/base-prices/${response.value.uuid}/edit`);

	await store.loadPlan(response.value.uuid);
	store.loadResolvedPlan(response.value.uuid);
};

/**
 * Saving writes a *new* plan and archives the one it came from, so the editor
 * has to move to the plan that was created — the route carries the uuid, and
 * staying put would leave it editing a version that is now archived.
 */
const save = async () => {
	const confirmed = await useDialog().confirm({
		title: "Save this configuration?",
		message:
			`"${store.plan?.name}" will be saved as a new version and opened, ` +
			"and the version you are editing will be archived.\n\n" +
			"Nothing is overwritten — the version being archived stays readable " +
			"exactly as it is now.",
		confirmButtonText: "Save version",
	});

	if (!confirmed) return;

	const saved = await store.savePlan();
	if (!saved) return;

	await router.replace(`/base-prices/${saved.uuid}/edit`);

	await store.loadPlan(saved.uuid);
	store.loadResolvedPlan(saved.uuid);
};
</script>

<template>
	<Teleport to="body">
		<div class="base-price-editor-fullscreen">
			<v-toolbar flat height="60">
				<div class="pl-5">
					<main-breadcrumbs :breadcrumbs="breadcrumbs" inline />
				</div>
				<v-spacer />

				<v-tooltip
					:text="
						store.hasAllocationErrors
							? 'Fix the issues in the header before saving'
							: 'Save this configuration as a new version and open it'
					"
					location="bottom"
				>
					<template v-slot:activator="{ props }">
						<div v-bind="props" class="mr-2">
							<v-btn
								color="primary"
								variant="tonal"
								:loading="store.saving"
								:disabled="store.hasAllocationErrors"
								@click="save"
							>
								Save version
							</v-btn>
						</div>
					</template>
				</v-tooltip>

				<v-btn
					size="40"
					variant="text"
					v-tooltip="`Open versions`"
					color="on-surface"
					data-testid="open-versions"
					@click="openVersions"
				>
					<v-icon size="20" icon="fa: fa-light fa-rectangle-list" />
				</v-btn>

				<v-divider vertical class="mx-2" />

				<v-btn
					size="40"
					variant="text"
					v-tooltip="`Close`"
					color="on-surface"
					data-testid="open-versions"
					@click="close"
				>
					<v-icon size="20" icon="fa: fa-regular fa-xmark" />
				</v-btn>
			</v-toolbar>
			<v-divider />

			<div
				v-if="store.loading"
				class="d-flex align-center justify-center h-100"
			>
				<v-progress-circular indeterminate color="primary" />
			</div>

			<div v-else class="base-price-editor-content">
				<plan-header-bar @refresh-impressions="refreshImpressions" />

				<div class="base-price-editor-body">
					<div class="base-price-editor-sidebar">
						<plan-hierarchy-sidebar />
					</div>
					<div class="base-price-editor-main">
						<plan-node-detail-panel />
					</div>
				</div>
			</div>

			<!--
				The secondary drawer, rendered here rather than teleported into
				BaseLayout's. This shell is itself a Teleport, and a second
				teleport inside or beside it had Vue reconciling anchors across
				detached subtrees — reliably "Cannot read properties of null
				(reading 'nextSibling')" the moment anything else in the editor
				re-rendered while it was open.

				Held in the DOM and slid out of view rather than mounted and
				unmounted, so opening and closing never patches this subtree's
				structure at all. It still runs off the shared baseStore flags,
				so anything that closes the app's drawer closes this one.
			-->
			<div
				v-show="useBaseStore().showSecondaryDrawer"
				class="editor-drawer-scrim"
				@click="useBaseStore().showSecondaryDrawer = false"
			/>
			<aside
				class="editor-drawer"
				:class="{
					'editor-drawer--open': useBaseStore().showSecondaryDrawer,
				}"
				:style="{ width: `${useBaseStore().secondaryDrawerWidth}px` }"
			>
				<component
					:is="useBaseStore().secondaryDrawerComponent"
					v-if="useBaseStore().secondaryDrawerComponent"
				/>
			</aside>

			<!--
				Every figure in here is recomputed by the resolver, and an edit
				reaches it on a debounce — so there is always a moment where the
				numbers on screen are the previous answer. Covering the page says
				that plainly, and stops an edit landing against figures that are
				about to change.

				`contained` keeps it inside this fullscreen shell rather than the
				document, which matters for the Budget books dialog: that renders
				above this stacking context, so it stays usable while the page
				behind it is busy. Blocking it would make editing a carve-out
				impossible, since every change re-resolves.
			-->
			<v-overlay
				:model-value="store.resolving"
				contained
				persistent
				no-click-animation
				class="align-center justify-center"
			>
				<div class="d-flex flex-column align-center ga-3">
					<v-progress-circular indeterminate color="primary" size="44" />
					<span class="text-caption text-medium-emphasis">Recalculating…</span>
				</div>
			</v-overlay>
		</div>
	</Teleport>
</template>

<style scoped lang="scss">
.base-price-editor-fullscreen {
	position: fixed;
	inset: 0;
	z-index: 1999;
	background: rgb(var(--v-theme-background));
	display: flex;
	flex-direction: column;
}

.base-price-editor-content {
	flex: 1;
	overflow: hidden;
	display: flex;
	flex-direction: column;
}

.base-price-editor-body {
	flex: 1;
	display: flex;
	min-height: 0;
}

.base-price-editor-sidebar {
	flex: 0 0 440px;
	border-right: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	min-height: 0;
}

.base-price-editor-main {
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

.editor-drawer-scrim {
	position: absolute;
	inset: 0;
	background: rgba(0, 0, 0, 0.3);
	z-index: 1;
}

.editor-drawer {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 2;
	max-width: 100%;
	background: rgb(var(--v-theme-surface));
	box-shadow: 0 0 14px rgba(0, 0, 0, 0.16);
	// The panel inside scrolls itself, so this stays a fixed frame — two
	// scrollbars over the same content would be one too many.
	overflow: hidden;
	// Parked off-screen rather than removed, so the panel's own state survives
	// being closed and reopened and nothing here mounts or unmounts.
	transform: translateX(100%);
	transition: transform 0.2s ease;
	visibility: hidden;
}

.editor-drawer--open {
	transform: translateX(0);
	visibility: visible;
}
</style>
